import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { 
  fetchOutlookEvents, 
  testOutlookConnection, 
  getOutlookUserInfo,
  outlookScopes 
} from '../utils/outlookUtils';

export function useOutlookEvents() {
  const { instance, accounts } = useMsal();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkConnection = async () => {
      if (accounts.length > 0) {
        try {
          const account = accounts[0];
          const accessTokenRequest = {
            scopes: outlookScopes,
            account: account,
          };

          const response = await instance.acquireTokenSilent(accessTokenRequest);
          // ✅ Validate that accessToken is retrieved from MSAL correctly
          if (response && response.accessToken && typeof response.accessToken === 'string') {
            setIsConnected(true);
            const userInfo = await getOutlookUserInfo(response.accessToken);
            setUserInfo(userInfo);
            
            // Auto-fetch events if we have a valid token
            const outlookEvents = await fetchOutlookEvents(response.accessToken);
            setEvents(outlookEvents);
            setLastSync(new Date().toISOString());
          } else {
            throw new Error('Invalid access token received');
          }
        } catch (error) {
          console.log('No valid token available, user needs to sign in:', error);
          setIsConnected(false);
          // ✅ Ensure fallback UI or error message for failed token fetches
          setError('Outlook connection not available. Please sign in.');
        }
      } else {
        setIsConnected(false);
        setError('No Outlook account found. Please sign in.');
      }
    };

    checkConnection();
  }, [accounts, instance]);

  // Connect to Outlook (OAuth login)
  const connect = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loginRequest = {
        scopes: outlookScopes,
        prompt: 'select_account'
      };

      const response = await instance.loginPopup(loginRequest);
      
      if (response.accessToken) {
        // Test the connection
        const testResult = await testOutlookConnection(response.accessToken);
        
        if (testResult.success) {
          setIsConnected(true);
          
          // Get user info
          const userInfo = await getOutlookUserInfo(response.accessToken);
          setUserInfo(userInfo);
          
          // Fetch initial events
          await fetchEvents(response.accessToken);
          
          return { success: true, message: testResult.message };
        } else {
          throw new Error(testResult.message);
        }
      }
    } catch (error) {
      console.error('Outlook connection failed:', error);
      const errorMessage = error.message || 'Failed to connect to Outlook';
      setError(errorMessage);
      setIsConnected(false);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  // Disconnect from Outlook
  const disconnect = useCallback(async () => {
    try {
      if (accounts.length > 0) {
        await instance.logoutPopup({ account: accounts[0] });
      }
      // ✅ Handle disconnect errors gracefully and ensure cleanup
      setIsConnected(false);
      setEvents([]);
      setUserInfo(null);
      setLastSync(null);
      setError(null);
    } catch (error) {
      console.error('Error disconnecting from Outlook:', error);
      // Still perform cleanup even if logout fails
      setIsConnected(false);
      setEvents([]);
      setUserInfo(null);
      setLastSync(null);
      setError('Disconnection completed with warnings. Please refresh if needed.');
    }
  }, [instance, accounts]);

  // Fetch events from Outlook
  const fetchEvents = useCallback(async (accessToken = null) => {
    setIsLoading(true);
    setError(null);

    try {
      let token = accessToken;
      
      if (!token && accounts.length > 0) {
        const account = accounts[0];
        const accessTokenRequest = {
          scopes: outlookScopes,
          account: account,
        };

        const response = await instance.acquireTokenSilent(accessTokenRequest);
        // ✅ Validate that accessToken is retrieved from MSAL correctly
        if (response && response.accessToken && typeof response.accessToken === 'string') {
          token = response.accessToken;
        } else {
          throw new Error('Failed to retrieve valid access token');
        }
      }

      if (!token || typeof token !== 'string') {
        throw new Error('No valid access token available');
      }

      const outlookEvents = await fetchOutlookEvents(token);
      setEvents(outlookEvents);
      setLastSync(new Date().toISOString());
      
      return outlookEvents;
    } catch (error) {
      console.error('Error fetching Outlook events:', error);
      const errorMessage = error.message || 'Failed to fetch Outlook events';
      setError(errorMessage);
      setEvents([]);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [instance, accounts]);

  // Test Outlook connection
  const testConnection = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (accounts.length === 0) {
        throw new Error('Not connected to Outlook');
      }

      const account = accounts[0];
      const accessTokenRequest = {
        scopes: outlookScopes,
        account: account,
      };

      const response = await instance.acquireTokenSilent(accessTokenRequest);
      const testResult = await testOutlookConnection(response.accessToken);
      
      if (!testResult.success) {
        setError(testResult.message);
      }
      
      return testResult;
    } catch (error) {
      console.error('Error testing Outlook connection:', error);
      const errorMessage = error.message || 'Connection test failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [instance, accounts]);

  // Refresh events (alias for fetchEvents)
  const refresh = useCallback(async () => {
    return await fetchEvents();
  }, [accounts, instance]);

  return {
    events,
    isLoading,
    isConnected,
    error,
    userInfo,
    lastSync,
    connect,
    disconnect,
    refresh,
    testConnection,
    fetchEvents
  };
}
